import type { RouteLocationRaw } from 'vue-router';

import type { FiscalShiftState, ShiftClosingReminder, ShiftClosingReminderStage } from '@/entities/fiscalization';
import type { LocationAttentionRow } from '@/entities/location';

import { plural } from '@/shared/lib';
import { ShiftSettlementState } from '@shared/api/graphql/gql/graphql';
import { Temporal, formatTime, inTimezone, parseInstant } from '@shared/lib/date-time';

export type PlaqueTone = 'danger' | 'success' | 'warning';

export interface PlaqueRow {
  id: string;
  tone: 'danger' | 'info' | 'warning';
  text: string;
  count: number;
  target: { kind: 'none' } | { kind: 'route'; to: RouteLocationRaw } | { kind: 'switch'; locationId: string };
}

export interface PlaqueGroup {
  id: string;
  title: string;
  rows: PlaqueRow[];
}

export interface PlaqueLocalShift {
  shiftState: FiscalShiftState | null;
  kktConcern: FiscalShiftState | null;
  reminder: ShiftClosingReminder | null;
  isWorkstationBound: boolean;
}

interface DotInput {
  shiftState: FiscalShiftState | null;
  kktConcern: FiscalShiftState | null;
  reminderStage: ShiftClosingReminderStage | null;
  isWorkstationBound: boolean;
}

const SHIFT_DANGER = new Set(['shift_expired', 'kkt_shift_orphan', 'shift_kkt_desync', 'shift_unknown']);
const SHIFT_WARNING = new Set(['shift_expiring', 'shift_opening', 'shift_closing']);
/** Эти «проблемы кассы» значат лишь, что касса не на этом компьютере. */
const KKT_ABSENT = new Set(['bridge_missing', 'workstation_not_bound']);

/** Тон смены без учёта кассы; null — режим смен выключен или данных нет. */
function shiftTone(
  shiftState: FiscalShiftState | null,
  reminderStage: ShiftClosingReminderStage | null,
): PlaqueTone | 'closed' | null {
  if (shiftState === null) return null;
  if (shiftState.status === 'shift_open') {
    return reminderStage === 'overdue' || reminderStage === 'critical' ? 'warning' : 'success';
  }
  if (SHIFT_WARNING.has(shiftState.status)) return 'warning';
  if (shiftState.status === 'shift_closed') return 'closed';
  if (SHIFT_DANGER.has(shiftState.status)) return 'danger';

  return null;
}

/** Тон кассы — только на компьютере кассы этой локации. */
function kktTone(input: Pick<DotInput, 'isWorkstationBound' | 'kktConcern'>): 'danger' | 'warning' | null {
  const concern = input.kktConcern;
  if (!input.isWorkstationBound || concern === null || KKT_ABSENT.has(concern.status)) return null;
  if (concern.tone === 'danger') return 'danger';

  return concern.tone === 'warning' ? 'warning' : null;
}

/** Цвет точки плашки: смена текущей локации, поднятая проблемой кассы этого компьютера; закрытая смена — без точки. */
export function plaqueDotTone(input: DotInput): PlaqueTone | null {
  const shift = shiftTone(input.shiftState, input.reminderStage);
  if (shift === null) return null;

  const kkt = kktTone(input);
  if (kkt === 'danger') return 'danger';
  if (kkt === 'warning' && (shift === 'success' || shift === 'closed')) return 'warning';

  return shift === 'closed' ? null : shift;
}

const paymentsRow = (locationId: string, count: number): PlaqueRow => ({
  id: `payments:${locationId}`,
  tone: 'warning',
  text: `${count} ${plural(count, ['платёж ждёт', 'платежа ждут', 'платежей ждут'])} решения`,
  count,
  target: { kind: 'route', to: { name: 'payments.index', query: { locationId } } },
});

const isClosedBySchedule = (row: LocationAttentionRow, now: Temporal.Instant): boolean => {
  const closesAt = parseInstant(row.location_closes_at);

  return closesAt !== null && Temporal.Instant.compare(closesAt, now) < 0;
};

function otherLocationRows(row: LocationAttentionRow, zone: string, now: Temporal.Instant): PlaqueRow[] {
  const rows: PlaqueRow[] = [];
  const target = { kind: 'switch', locationId: row.location_id } as const;
  if (row.shift_state === ShiftSettlementState.Warning) {
    rows.push({
      id: `shift-long:${row.location_id}`,
      tone: 'warning',
      text: 'Смена идёт больше 20 часов',
      count: 1,
      target,
    });
  }
  if (row.shift_state === ShiftSettlementState.Expired) {
    rows.push({ id: `shift-expired:${row.location_id}`, tone: 'danger', text: 'Смена истекла', count: 1, target });
  }
  if (
    (row.shift_state === ShiftSettlementState.Open || row.shift_state === ShiftSettlementState.Warning) &&
    isClosedBySchedule(row, now)
  ) {
    rows.push({
      id: `shift-unclosed:${row.location_id}`,
      tone: 'warning',
      text: `Смена не закрыта после ${formatTime(inTimezone(row.location_closes_at ?? '', zone))}`,
      count: 1,
      target,
    });
  }
  if (row.payment_attention_count > 0) rows.push(paymentsRow(row.location_id, row.payment_attention_count));

  return rows;
}

function currentLocationRows(
  locationId: string,
  local: PlaqueLocalShift,
  row: LocationAttentionRow | undefined,
): PlaqueRow[] {
  const rows: PlaqueRow[] = [];
  const shift = shiftTone(local.shiftState, local.reminder?.stage ?? null);
  if (local.shiftState !== null && (shift === 'warning' || shift === 'danger')) {
    const reminderTitle =
      local.reminder?.stage === 'overdue' || local.reminder?.stage === 'critical' ? local.reminder.title : null;
    rows.push({
      id: `shift:${locationId}`,
      tone: shift,
      text: reminderTitle ?? local.shiftState.title,
      count: 1,
      target: { kind: 'none' },
    });
  }
  const kkt = kktTone(local);
  if (kkt !== null && local.kktConcern !== null) {
    rows.push({
      id: `kkt:${locationId}`,
      tone: kkt,
      text: `Касса: ${local.kktConcern.title}`,
      count: 1,
      target: { kind: 'none' },
    });
  }
  if (row !== undefined && row.payment_attention_count > 0)
    rows.push(paymentsRow(locationId, row.payment_attention_count));

  return rows;
}

export interface PlaqueAttentionInput {
  currentLocationId: string | null;
  locations: { id: string; title: string; timezone: string }[];
  attention: Map<string, LocationAttentionRow>;
  local: PlaqueLocalShift;
  canApproveRefunds: boolean;
  now: Temporal.Instant;
}

/** «Нужно решить»: текущая локация, другие доступные, запросы на возврат управляющему. */
export function buildPlaqueAttention(input: PlaqueAttentionInput): {
  total: number;
  tone: 'danger' | 'warning' | null;
  groups: PlaqueGroup[];
} {
  const groups: PlaqueGroup[] = [];
  const current = input.locations.find((location) => location.id === input.currentLocationId);
  if (current !== undefined) {
    groups.push({
      id: current.id,
      title: `${current.title}, здесь`,
      rows: currentLocationRows(current.id, input.local, input.attention.get(current.id)),
    });
  }
  for (const location of input.locations) {
    const row = input.attention.get(location.id);
    if (location.id === input.currentLocationId || row === undefined) continue;
    groups.push({ id: location.id, title: location.title, rows: otherLocationRows(row, location.timezone, input.now) });
  }
  const refunds = [...input.attention.values()].reduce((sum, row) => sum + row.refund_approval_count, 0);
  if (input.canApproveRefunds && refunds > 0) {
    groups.push({
      id: 'manager',
      title: 'Управляющему',
      rows: [
        {
          id: 'refund-approvals',
          tone: 'info',
          text: `${refunds} ${plural(refunds, ['запрос', 'запроса', 'запросов'])} на возврат`,
          count: refunds,
          target: { kind: 'route', to: { name: 'payments.index' } },
        },
      ],
    });
  }

  const visible = groups.filter((group) => group.rows.length > 0);
  const rows = visible.flatMap((group) => group.rows);
  const total = rows.reduce((sum, row) => sum + row.count, 0);
  const tone = rows.some((row) => row.tone === 'danger') ? 'danger' : total > 0 ? 'warning' : null;

  return { total, tone, groups: visible };
}

/** Подпись смены в списке локаций; закрытая смена — норма, без подписи. */
export function locationShiftCaption(
  row: LocationAttentionRow | undefined,
  now: Temporal.Instant,
  zone: string,
): string {
  switch (row?.shift_state) {
    case ShiftSettlementState.Opening:
      return 'смена открывается';
    case ShiftSettlementState.Open:
      return isClosedBySchedule(row, now)
        ? `не закрыта после ${formatTime(inTimezone(row.location_closes_at ?? '', zone))}`
        : 'смена идёт';
    case ShiftSettlementState.Warning:
      return 'смена скоро истечёт';
    case ShiftSettlementState.Closing:
      return 'смена закрывается';
    case ShiftSettlementState.Expired:
      return 'смена истекла';
    default:
      return '';
  }
}

/** Точка смены на бейдже локации в списке, как у плашки; закрытая смена — без точки. */
export function locationShiftDotTone(row: LocationAttentionRow | undefined, now: Temporal.Instant): PlaqueTone | null {
  switch (row?.shift_state) {
    case ShiftSettlementState.Open:
      return isClosedBySchedule(row, now) ? 'warning' : 'success';
    case ShiftSettlementState.Opening:
    case ShiftSettlementState.Warning:
    case ShiftSettlementState.Closing:
      return 'warning';
    case ShiftSettlementState.Expired:
      return 'danger';
    default:
      return null;
  }
}

/** «MetaRacing ТК Питер» → «ТК Питер»: организация уже подписана в панели. */
export function withoutOrganizationPrefix(title: string, organizationTitle: string): string {
  const prefix = `${organizationTitle} `.toLocaleLowerCase('ru');
  const rest = title.slice(prefix.length).trim();

  return organizationTitle !== '' && title.toLocaleLowerCase('ru').startsWith(prefix) && rest !== '' ? rest : title;
}
